import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useResourcesApi } from '@/services/api/resourcesApi'
import {
    AddressData,
    AddressOption,
    AddressValidationRules
} from '@/types/forms/addressForm'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

// Separate hook for managing API catalog data
const useAddressCatalogData = () => {
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const [streetTypes, setStreetTypes] = useState<any[]>([])
    const [streetQualifiers, setStreetQualifiers] = useState<any[]>([])
    const [streetOrientations, setStreetOrientations] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const hasFetched = useRef(false)

    useEffect(() => {
        if (hasFetched.current) {
            return
        }

        hasFetched.current = true

        const processResponse = (
            response: any,
            setter: (data: any[]) => void
        ) => {
            if (response?.success) {
                setter(response.data || [])
            } else if (response?.data) {
                setter(response.data)
            }
        }

        const fetchAddressData = async () => {
            try {
                setIsLoading(true)

                const [
                    typesResponse,
                    qualifiersResponse,
                    orientationsResponse
                ] = await Promise.all([
                    resourcesApi.getStreetTypes(),
                    resourcesApi.getStreetQualifiers(),
                    resourcesApi.getStreetOrientations()
                ])

                processResponse(typesResponse, setStreetTypes)
                processResponse(qualifiersResponse, setStreetQualifiers)
                processResponse(orientationsResponse, setStreetOrientations)
            } catch (error) {
                console.error('Error fetching address catalog data:', error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchAddressData()
    }, [])

    return {
        streetTypes,
        streetQualifiers,
        streetOrientations,
        isLoading
    }
}

// Separate hook for address string parsing functionality
const useAddressParser = (addressOptions: {
    roadTypes: AddressOption[]
    appendices: AddressOption[]
    orientations: AddressOption[]
}) => {
    const findRoadType = useCallback(
        (address: string) => {
            return addressOptions.roadTypes.find((type) =>
                address.toLowerCase().includes(type.label.toLowerCase())
            )
        },
        [addressOptions.roadTypes]
    )

    const findOrientation = useCallback(
        (address: string) => {
            return addressOptions.orientations.find((opt) =>
                address.toLowerCase().includes(opt.label.toLowerCase())
            )
        },
        [addressOptions.orientations]
    )

    const findAppendix = useCallback(
        (appendix: string) => {
            return addressOptions.appendices.find(
                (opt) => opt.label === appendix || opt.value === appendix
            )
        },
        [addressOptions.appendices]
    )

    const parseRoadInfo = useCallback(
        (address: string, parsed: Partial<AddressData>) => {
            const roadType = findRoadType(address)

            if (roadType) {
                parsed.roadType = roadType.value
            }

            const roadNumberMatch = address.match(/(\d+)/)

            if (roadNumberMatch) {
                parsed.roadNumber = roadNumberMatch[1]
            }

            const appendixMatch = address.match(/(\d+)\s*([A-Z]+)/)

            if (appendixMatch) {
                const appendix = appendixMatch[2]
                const appendixOption = findAppendix(appendix)

                if (appendixOption) {
                    parsed.roadAppendix = appendixOption.value
                }
            }

            const orientation = findOrientation(address)

            if (orientation) {
                parsed.roadOrientation = orientation.value
            }
        },
        [findRoadType, findOrientation, findAppendix]
    )

    const parseCrossInfo = useCallback(
        (address: string, parsed: Partial<AddressData>) => {
            const crossMatch = address.match(/#\s*(\d+)/)

            if (crossMatch) {
                parsed.crossNumber = crossMatch[1]
            }

            const crossAppendixMatch = address.match(/#\s*\d+\s*([A-Z]+)/)

            if (crossAppendixMatch) {
                const crossAppendix = crossAppendixMatch[1]
                const crossAppendixOption = findAppendix(crossAppendix)

                if (crossAppendixOption) {
                    parsed.crossAppendix = crossAppendixOption.value
                }
            }

            const crossOrientation = findOrientation(address)

            if (
                crossOrientation &&
                crossOrientation.value !== parsed.roadOrientation
            ) {
                parsed.crossOrientation = crossOrientation.value
            }
        },
        [findOrientation, findAppendix]
    )

    const parseAddressString = useCallback(
        (addressString: string): Partial<AddressData> => {
            if (!addressString || !addressString.trim()) {
                return {}
            }

            const parsed: Partial<AddressData> = {}
            const address = addressString.trim()

            parseRoadInfo(address, parsed)
            parseCrossInfo(address, parsed)

            // Extract additional number from address
            const additionalMatch = address.match(/-(\d+)/)

            if (additionalMatch) {
                parsed.additionalNumber = additionalMatch[1]
            }

            // Extract complement from address string
            const complementWithCrossMatch = address.match(
                /(?:#\s*\d+(?:[A-Z]*\s*[A-Z]*)?(?:-\d+)?\s*)(.+)$/
            )

            if (complementWithCrossMatch) {
                parsed.complement = complementWithCrossMatch[1].trim()
            } else {
                // Handle complement extraction for addresses without cross number
                const simpleComplementMatch = address.match(
                    /(?:\d+[A-Z]*\s*[A-Z]*\s*)(.+)$/
                )

                if (simpleComplementMatch) {
                    parsed.complement = simpleComplementMatch[1].trim()
                }
            }

            return parsed
        },
        [parseRoadInfo, parseCrossInfo]
    )

    return { parseAddressString }
}

// Separate hook for form validation rules
const useAddressValidation = (t: any, department?: string) => {
    return useMemo(
        (): AddressValidationRules => ({
            roadType: {
                required: t('common.required')
            },
            roadNumber: {
                required: t('common.required'),
                pattern: {
                    value: /^[0-9]+$/,
                    message: t('common.numberOnly')
                }
            },
            crossNumber: {
                required: t('common.required'),
                pattern: {
                    value: /^[0-9]+$/,
                    message: t('common.numberOnly')
                }
            },
            additionalNumber: {
                required:
                    department === '11' ||
                    department?.toUpperCase().includes('BOGOTA') ||
                    department?.toUpperCase().includes('BOGOTÁ')
                        ? t('common.required')
                        : false,
                pattern: {
                    value: /^[0-9]+$/,
                    message: t('common.numberOnly')
                }
            }
        }),
        [t, department]
    )
}

// Separate hook for address string generation
const useAddressGeneration = (addressOptions: {
    roadTypes: AddressOption[]
    appendices: AddressOption[]
    orientations: AddressOption[]
}) => {
    const getRoadTypeName = useCallback(
        (roadType: string) => {
            return (
                addressOptions.roadTypes.find((type) => type.value === roadType)
                    ?.label || roadType
            )
        },
        [addressOptions.roadTypes]
    )

    const getOrientationName = useCallback(
        (orientationValue: string) => {
            return (
                addressOptions.orientations.find(
                    (orientation) => orientation.value === orientationValue
                )?.label || orientationValue
            )
        },
        [addressOptions.orientations]
    )

    const generateRoadPart = useCallback(
        (data: AddressData): string => {
            const roadTypeName = getRoadTypeName(data.roadType)
            let roadPart = `${roadTypeName} ${data.roadNumber}`

            if (data.roadAppendix) {
                roadPart += ` ${data.roadAppendix}`
            }

            if (data.roadOrientation) {
                const orientationName = getOrientationName(data.roadOrientation)

                roadPart += ` ${orientationName}`
            }

            return roadPart
        },
        [getRoadTypeName, getOrientationName]
    )

    const generateCrossPart = useCallback(
        (data: AddressData): string => {
            let crossPart = `# ${data.crossNumber}`

            if (data.crossAppendix) {
                crossPart += ` ${data.crossAppendix}`
            }

            if (data.crossOrientation) {
                const orientationName = getOrientationName(
                    data.crossOrientation
                )

                crossPart += ` ${orientationName}`
            }

            if (data.additionalNumber) {
                crossPart += `-${data.additionalNumber}`
            }

            return crossPart
        },
        [getOrientationName]
    )

    const generateAddressString = useCallback(
        (data: AddressData): string => {
            const parts: string[] = []

            if (data.roadType && data.roadNumber) {
                parts.push(generateRoadPart(data))
            }

            if (data.crossNumber) {
                parts.push(generateCrossPart(data))
            }

            if (data.complement && data.complement.trim()) {
                parts.push(data.complement.trim())
            }

            return parts.join(' ')
        },
        [generateRoadPart, generateCrossPart]
    )

    return {
        generateAddressString,
        generateRoadPart,
        generateCrossPart
    }
}

export const useAddressEditor = (initialValue?: string, department?: string) => {
    const { t } = useTranslation()
    const { streetTypes, streetQualifiers, streetOrientations, isLoading } =
        useAddressCatalogData()

    // Initialize form with default values
    const form = useForm<AddressData>({
        defaultValues: {
            roadType: '',
            roadNumber: '',
            roadAppendix: '',
            roadOrientation: '',
            crossNumber: '',
            crossAppendix: '',
            crossOrientation: '',
            additionalNumber: '',
            complement: ''
        }
    })

    const { control, watch, reset, getValues, setValue } = form
    const watchedValues = watch()

    // Transform API response data to AddressOption format
    const addressOptions = useMemo(
        (): {
            roadTypes: AddressOption[]
            appendices: AddressOption[]
            orientations: AddressOption[]
        } => ({
            roadTypes: streetTypes.map((item) => ({
                value: item.code,
                label: item.name
            })),
            appendices: streetQualifiers.map((item) => ({
                value: item.code,
                label: item.name
            })),
            orientations: streetOrientations.map((item) => ({
                value: item.code,
                label: item.name
            }))
        }),
        [streetTypes, streetQualifiers, streetOrientations]
    )

    // Verify if catalog data is available
    const hasData =
        streetTypes.length > 0 ||
        streetQualifiers.length > 0 ||
        streetOrientations.length > 0

    // Initialize address processing utilities
    const { parseAddressString } = useAddressParser(addressOptions)
    const { generateAddressString } = useAddressGeneration(addressOptions)
    const validationRules = useAddressValidation(t, department)

    // Generate real-time address preview
    const addressPreview = useMemo(() => {
        return generateAddressString(watchedValues)
    }, [watchedValues, generateAddressString])

    // Parse and populate form with initial address value
    useEffect(() => {
        if (initialValue && hasData && addressOptions.roadTypes.length > 0) {
            const parsedData = parseAddressString(initialValue)

            Object.entries(parsedData).forEach(([key, value]) => {
                if (value) {
                    setValue(key as keyof AddressData, value)
                }
            })
        }
    }, [initialValue, hasData, addressOptions, parseAddressString, setValue])

    // Reset all form fields to empty state
    const clearForm = useCallback(() => {
        reset({
            roadType: '',
            roadNumber: '',
            roadAppendix: '',
            roadOrientation: '',
            crossNumber: '',
            crossAppendix: '',
            crossOrientation: '',
            additionalNumber: '',
            complement: ''
        })
    }, [reset])

    // Ensure cross number validation requires complete road information
    const validateCrossNumber = useCallback(() => {
        const values = getValues()

        if (values.crossNumber && (!values.roadType || !values.roadNumber)) {
            return t('address.crossNumberRequiresRoad')
        }

        return true
    }, [getValues, t])

    // Reset form fields with parsed address data
    const resetForm = useCallback(
        (newInitialValue?: string) => {
            if (
                newInitialValue &&
                hasData &&
                addressOptions.roadTypes.length > 0
            ) {
                const parsedData = parseAddressString(newInitialValue)

                Object.entries(parsedData).forEach(([key, value]) => {
                    if (value) {
                        setValue(key as keyof AddressData, value)
                    }
                })
            }
        },
        [hasData, addressOptions, parseAddressString, setValue]
    )

    return {
        form,
        control,
        addressOptions,
        validationRules,
        addressPreview,
        generateAddressString,
        clearForm,
        validateCrossNumber,
        getValues,
        isLoading,
        hasData,
        resetForm
    }
}
